import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Panel } from "@/components/admin/common";

const TOGGLES = [
  ["email_enabled", "Email the selected package itinerary to the customer immediately"],
  ["whatsapp_enabled", "WhatsApp enquiry confirmation + itinerary link to the customer"],
  ["owner_whatsapp_alert", "WhatsApp new-lead alert to the owner alert number"],
];

const TemplateCard = ({ t, onChange }) => (
  <Panel className="p-5 space-y-3" data-testid={`auto-template-${t.key.replace("/", "-")}`}>
    <p className="font-semibold text-navy">{t.key === "general" ? "No package selected" : `${t.key} packages`}</p>
    <div className="space-y-1.5"><Label>Email subject</Label>
      <Input value={t.subject} maxLength={200} onChange={(e) => onChange({ ...t, subject: e.target.value })} data-testid={`auto-template-subject-${t.key.replace("/", "-")}`} /></div>
    <div className="space-y-1.5"><Label>Message (shown above the day-wise itinerary)</Label>
      <Textarea rows={6} maxLength={3000} value={t.body} onChange={(e) => onChange({ ...t, body: e.target.value })} data-testid={`auto-template-body-${t.key.replace("/", "-")}`} /></div>
  </Panel>
);

export default function AutoMessages() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/auto-messages").then((r) => setC(r.data)); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/auto-messages", { email_enabled: c.email_enabled, whatsapp_enabled: c.whatsapp_enabled, owner_whatsapp_alert: c.owner_whatsapp_alert, templates: c.templates.map(({ key, subject, body }) => ({ key, subject, body })) });
      setC(data);
      toast.success("Auto messages saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  if (!c) return <Loader2 className="h-6 w-6 animate-spin text-ocean" />;
  return (
    <div data-testid="admin-auto-messages-page">
      <PageHeader title="Enquiry Auto Messages" subtitle="What every new website enquiry automatically receives. The day-wise itinerary and PDF link come from the selected package.">
        <Button onClick={save} disabled={saving} className="bg-ocean hover:bg-navy" data-testid="auto-messages-save-button">Save changes</Button>
      </PageHeader>
      <Panel className="p-5 mb-6 space-y-3">
        {TOGGLES.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 p-3">
            <span className="text-sm text-slate-700">{label}</span>
            <Switch checked={!!c[k]} onCheckedChange={(v) => setC((s) => ({ ...s, [k]: v }))} data-testid={`auto-toggle-${k}`} />
          </div>
        ))}
        <p className={`text-xs ${c.whatsapp_connected ? "text-emerald-700" : "text-amber-700"}`} data-testid="auto-whatsapp-status">
          {c.whatsapp_connected ? "WhatsApp is connected. Make sure templates owt_enquiry_received and owt_new_lead_alert are approved (see WhatsApp Automation)."
            : "WhatsApp is not connected yet, so only emails are sent. WhatsApp messages start automatically once connected and the templates are approved."}
        </p>
        <p className="text-xs text-slate-500">Placeholders: {c.placeholders.join(", ")}</p>
      </Panel>
      <div className="grid gap-6 lg:grid-cols-2">
        {c.templates.map((t, i) => <TemplateCard key={t.key} t={t} onChange={(nt) => setC((s) => ({ ...s, templates: s.templates.map((x, j) => (j === i ? nt : x)) }))} />)}
      </div>
    </div>
  );
}
